/**
 * =============================================================================
 * Hook: useCommissionPayoutsDetail
 * Versão: 1.0.0 (2026-04-30)
 *
 * Descrição:
 *   Drill-down inverso de `useReferralPayoutDetails` — lista TODOS os pagamentos
 *   vinculados a uma comissão (incluindo o histórico de links liberados).
 *
 *   Usa a RPC `get_commission_payouts_detail`, que pivota em
 *   `affiliate_payout_commission_links` (relação N:N), permitindo visualizar
 *   quantas vezes a comissão entrou em algum pagamento.
 *
 *   - Server-side, com validação de tenant + autorização no backend.
 *   - Cache padrão TanStack Query (30s staleTime).
 * =============================================================================
 */
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { createLogger } from '@/lib/logger';

const logger = createLogger('CommissionPayoutsDetail');

/** Estrutura flat de cada pagamento vinculado à comissão. */
export interface CommissionPayoutDetail {
  link_id: string;
  payout_id: string;
  payout_status: string;
  payout_amount: number;
  payout_currency: string;
  payout_method: string | null;
  external_transaction_id: string | null;
  requested_at: string | null;
  paid_at: string | null;
  failed_reason: string | null;
  link_reward_value: number;
  linked_at: string;
  released_at: string | null;
  released_reason: string | null;
  /** `true` quando o link está ATIVO (released_at IS NULL). */
  is_active: boolean;
}

/**
 * Hook principal — busca todos os pagamentos vinculados a uma comissão.
 *
 * @param commissionId UUID da comissão (ou null para desabilitar a query).
 */
export function useCommissionPayoutsDetail(commissionId: string | null) {
  const { currentTenant } = useAuth();

  return useQuery({
    queryKey: ['commission-payouts-detail', currentTenant?.id, commissionId],
    queryFn: async (): Promise<CommissionPayoutDetail[]> => {
      if (!commissionId) return [];

      logger.debug('Buscando pagamentos da comissão', { commissionId });

      // Casting necessário até `types.ts` ser regenerado
      const { data, error } = await (supabase.rpc as any)(
        'get_commission_payouts_detail',
        {
          p_commission_id: commissionId,
          p_tenant_hint: currentTenant?.id || null,
        },
      );

      if (error) {
        logger.error('Erro ao buscar pagamentos da comissão', error);
        throw error;
      }

      return (data || []) as CommissionPayoutDetail[];
    },
    enabled: !!commissionId && !!currentTenant?.id,
    staleTime: 30 * 1000,
    gcTime: 5 * 60 * 1000,
  });
}
